import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Item } from '../ItemManagement';
import {
  deleteBusinessPlacesAPI,
  deleteDigitalDevicesAPI,
  postBusinessPlacesAPI,
  postDigitalDevicesAPI,
  putBusinessPlacesAPI,
  putDigitalDevicesAPI
} from 'apis/item/itemAPI';
import { useTranslation } from 'react-i18next';

interface EmployeePopupProps {
  isOpen: boolean;
  handleClose: () => void;
  title?: string;
  itemData?: {
    id: number;
    name: string;
    nameEnglish: string;
    nameJapanese: string;
  } | null; // userData가 null일 수 있으므로 null 허용
  handleDeleteItem: (itemId: number) => void; // 삭제 처리 함수
  updateItems: (item: any, title: string) => void; // 추가/변경 함수
  itemListLength: number | null;
}

export default function ItemPopup({ isOpen, handleClose, title, itemData, handleDeleteItem, updateItems, itemListLength }: EmployeePopupProps) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); // 삭제 확인 팝업 상태
  const [formData, setFormData] = useState({
    id: itemData?.id || '',
    name: itemData?.name || '',
    nameEnglish: itemData?.nameEnglish || '',
    nameJapanese: itemData?.nameJapanese || ''
  });
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  // 추가 버튼 클릭
  const handleAddBtn = async () => {
    const newItem = {
      id: (itemListLength ?? 0) + 1, // itemListLength가 null이면 0으로 처리
      name: formData.name,
      nameEnglish: formData.nameEnglish,
      nameJapanese: formData.nameJapanese
    };
    try {
      let response;
      // title에 따라 API 호출을 분리
      if (title === '디지털기기') {
        response = await postDigitalDevicesAPI(newItem); // 디지털 기기 API 호출
        // console.log('handleAddBtn 디지털기기: ', response);
      } else if (title === '사업장') {
        response = await postBusinessPlacesAPI(newItem); // 사업장 API 호출
        // console.log('handleAddBtn 사업장: ', response);
      } else {
        throw new Error('유효하지 않은 title입니다.'); // title이 다른 경우 예외 처리
      }

      // 공통 후처리
      updateItems(newItem, title || ''); // 부모 컴포넌트에 새 아이템 전달
      handleClose(); // 팝업 닫기
    } catch (error: any) {
      // console.error('API 호출 중 에러 발생: ', error);
      console.error(error);
      return;
    }
  };

  // 삭제 버튼 클릭 시 삭제 확인 팝업 열기
  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  // 삭제 확인 팝업에서 확인 클릭 시
  const handleConfirmDelete = async () => {
    if (itemData) {
      try {
        // 삭제 API
        let response;
        // title에 따라 API 호출을 분리
        if (title === '디지털기기') {
          response = await deleteDigitalDevicesAPI(itemData.id); // 디지털 기기 API 호출
        } else if (title === '사업장') {
          response = await deleteBusinessPlacesAPI(itemData.id); // 사업장 API 호출
        } else {
          throw new Error('유효하지 않은 title입니다.'); // title이 다른 경우 예외 처리
        }
      } catch (error: any) {
        return;
      }
      handleDeleteItem(itemData?.id); // 삭제 함수 호출
      handleClose(); // 팝업 닫기
    }
    setConfirmDeleteOpen(false); // 삭제 여부 팝업 닫기
    handleClose(); // 메인 팝업 닫기
  };

  // 삭제 확인 팝업에서 취소 클릭 시
  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false); // 삭제 여부 팝업 닫기
  };

  // 변경 버튼 클릭
  const handleChangeBtn = async () => {
    if (itemData) {
      const updatedItem: Item = {
        id: itemData.id, // 기존 ID 유지
        name: formData.name,
        nameEnglish: formData.nameEnglish,
        nameJapanese: formData.nameJapanese
      };
      const updatedItemAPI = {
        name: formData.name,
        nameEnglish: formData.nameEnglish,
        nameJapanese: formData.nameJapanese
      };

      // API
      try {
        // 삭제 API
        let response;
        // title에 따라 API 호출을 분리
        if (title === '디지털기기') {
          response = await putDigitalDevicesAPI(itemData.id, updatedItemAPI); // 디지털 기기 API 호출
        } else if (title === '사업장') {
          response = await putBusinessPlacesAPI(itemData.id, updatedItemAPI); // 사업장 API 호출
        } else {
          throw new Error('유효하지 않은 title입니다.'); // title이 다른 경우 예외 처리
        }
      } catch (error: any) {
        return;
      }

      updateItems(updatedItem, title || ''); // 부모 컴포넌트에 변경된 아이템 전달
      handleClose(); // 팝업 닫기
    }
  };

  const handleCloseAndReset = () => {
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };

  useEffect(() => {
    if (isOpen) {
      setFormData({
        id: itemData?.id || '',
        name: itemData?.name || '',
        nameEnglish: itemData?.nameEnglish || '',
        nameJapanese: itemData?.nameJapanese || ''
      });
    }
  }, [itemData, isOpen]); // isOpen이 바뀔 때마다 formData를 초기화

  // 입력 필드 변경 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAndReset}
      sx={{
        width: { xs: '90%', sm: '700px' }, // 모바일에서는 90% 너비
        margin: 'auto'
      }}
    >
      <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
        <span>{title ? `${title} ${t('item_popup.add_edit')}` : itemData?.name}</span>

        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          width: { xs: '100%', sm: '400px' }, // 모바일에서는 가로 제한 없음
          bgcolor: theme.palette.contentBlock.background,
          paddingY: '20px !important'
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          {/* 이름(한국어) */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={90} variant="caption" color={theme.palette.fieldText.title}>
              {t('item_popup.name_ko')}
            </Typography>
            <TextField name="name" value={formData.name} size="small" required onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </Box>
          {/* 이름(영어) */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={90} variant="caption" color={theme.palette.fieldText.title}>
              {t('item_popup.name_en')}
            </Typography>
            <TextField name="nameEnglish" value={formData.nameEnglish} size="small" required onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </Box>
          {/* 이름(일본어) */}
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={90} variant="caption" color={theme.palette.fieldText.title}>
              {t('item_popup.name_ja')}
            </Typography>
            <TextField name="nameJapanese" value={formData.nameJapanese} size="small" required onChange={handleInputChange} sx={{ flexGrow: 1 }} />
          </Box>
        </Box>
      </DialogContent>

      {/* 버튼들 */}
      <DialogActions sx={{ padding: 2, paddingTop: 0, bgcolor: theme.palette.contentBlock.background }}>
        {itemData ? (
          <>
            {/* 삭제 */}
            <Button
              variant="contained"
              color="error" // 삭제 버튼 빨간색
              sx={{ borderRadius: '4px', fontWeight: 'bold' }}
              onClick={handleDeleteClick}
              fullWidth
            >
              {t('item_popup.btn.delete')}
            </Button>
            {/* 변경 */}
            <Button variant="contained" color="primary" sx={{ borderRadius: '4px', fontWeight: 'bold' }} onClick={handleChangeBtn} fullWidth>
              {t('item_popup.btn.change')}
            </Button>
          </>
        ) : (
          // 추가
          <Button variant="contained" color="primary" sx={{ borderRadius: '4px', fontWeight: 'bold' }} onClick={handleAddBtn} fullWidth>
            {t('item_popup.btn.add')}
          </Button>
        )}
      </DialogActions>

      {/* 삭제 확인 팝업 */}
      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
        <DialogContent>{t('item_popup.ask_delete')}</DialogContent>
        <DialogActions>
          {/* 취소 */}
          <Button onClick={handleCancelDelete} color="error">
            {t('item_popup.btn.cancel')}
          </Button>
          {/* 확인 */}
          <Button onClick={handleConfirmDelete} color="primary">
            {t('item_popup.btn.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
